import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import LogoutModal from '../components/common/LogoutModal';
import LoadingModal from '../components/common/LoadingModal';
import { useSelector, useDispatch } from 'react-redux';
import { apiInvoker } from '../lib/apiInvoker';
import { API_URLS } from '../lib/apiUrls';
import { BackIcon, CrossIcon, EditIcon, MobileMenuIcon, OutlineIcon, StarrIcon } from '../assets/svgs/svgs';
import { logoutUser } from '../store/actions/authActions';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';

const AIOutline = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formData, claimedTopics, notes, selectedTopic, outlineId, researchId, scripts } = location.state || {};

  const dispatch = useDispatch();
  const [selectedHooks, setSelectedHooks] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingHook, setEditingHook] = useState(null);
  const [editData, setEditData] = useState({
    Title: '',
    Introduction: '',
    Conclusion: '',
    CallToAction: '',
    Sections: []
  });
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [sectionEditData, setSectionEditData] = useState({
    SectionTitle: '',
    Explanation: '',
    StepByStep: [],
    CommonMistakes: [],
    Example: '',
    RetentionMoments: []
  });
  const [showTopicDetailsModal, setShowTopicDetailsModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const researchReportState = useSelector((state) => state.researchReport);
  const loading = researchReportState?.loading || false;
  const error = researchReportState?.error || null;

  const [hooks, setHooks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 2;
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(() => {
    if (scripts && Array.isArray(scripts) && scripts.length > 0) {
      const hasValidScripts = scripts.some(script => 
        script && typeof script === 'object' && (script.Title || script.Introduction || script.Sections || Object.keys(script).some(key => key.startsWith('script')))
      );
      return !hasValidScripts;
    }
    return true;
  });
  const isGeneratingScriptRef = useRef(false);
  const scriptGenerationTimeoutRef = useRef(null);
  const scriptGenerationDataRef = useRef(null);
  const hasLoadedDataRef = useRef(false);

  const getCurrentResearchId = () => {
    return researchId || 
           localStorage.getItem('currentResearchId') ||
           null;
  };

  const currentResearchId = getCurrentResearchId();

const transformScriptsToHooks = (scripts) => {
    if (!scripts || !Array.isArray(scripts) || scripts.length === 0) return [];
    
    const transformedHooks = [];
    
    scripts.forEach((script, index) => {
      if (script && typeof script === 'object') {
        if (script.Title || script.Introduction || script.Sections) {
          transformedHooks.push({
            id: script._id?.toString() || script.id || `script-${index + 1}`,
            title: script.Title || `Script ${index + 1}:`,
            introduction: script.Introduction || '',
            sections: script.Sections || [],
            conclusion: script.Conclusion || '',
            callToAction: script.CallToAction || '',
            hook: script.Introduction || '',
            text: script.Introduction || '',
            originalText: script.Introduction || ''
          });
        } else {
          const scriptKeys = Object.keys(script).filter(key => key.startsWith('script'));
          scriptKeys.forEach((key, keyIndex) => {
            const scriptItem = script[key];
            if (scriptItem) {
              transformedHooks.push({
                id: scriptItem._id?.toString() || scriptItem.id || `script-${index}-${keyIndex + 1}`,
                title: `Script ${transformedHooks.length + 1}:`,
                introduction: scriptItem.Introduction || '',
                sections: scriptItem.Sections || [],
                conclusion: scriptItem.Conclusion || '',
                callToAction: scriptItem.CallToAction || '',
                hook: scriptItem.Introduction || '',
                text: scriptItem.Introduction || '',
                originalText: scriptItem.Introduction || ''
              });
            }
          });
        }
      }
    });
    
    return transformedHooks;
  };

  const getDefaultHooks = () => [
    {
      id: 1,
      title: 'Script 1:',
      introduction: 'From AI-powered editing to ultra-close cinematic shots, 2025 is bringing a revolution to wildlife photography!',
      sections: [],
      conclusion: '',
      callToAction: '',
      hook: 'From AI-powered editing to ultra-close cinematic shots, 2025 is bringing a revolution to wildlife photography!',
      text: 'From AI-powered editing to ultra-close cinematic shots, 2025 is bringing a revolution to wildlife photography!',
      originalText: 'From AI-powered editing to ultra-close cinematic shots, 2025 is bringing a revolution to wildlife photography!'
    }
  ];



  useEffect(() => {
    if (hooks.length > 0 && currentPage > Math.ceil(hooks.length / itemsPerPage)) {
      setCurrentPage(1);
    }
  }, [hooks.length]);

  useEffect(() => {
    if (researchId) {
      localStorage.setItem('currentResearchId', researchId);
    }
  }, [researchId]);

  useEffect(() => {
    if (scripts && Array.isArray(scripts) && scripts.length > 0) {
      const transformedHooks = transformScriptsToHooks(scripts);
      if (transformedHooks.length > 0) {
        setHooks(transformedHooks);
        setIsInitialLoading(false);
        hasLoadedDataRef.current = true;
        return;
      }
    }
  }, []);

  useEffect(() => {
    const fetchResearchData = async () => {
      if (hasLoadedDataRef.current || hooks.length > 0) {
        setIsInitialLoading(false);
        return;
      }
      
      const activeResearchId = researchId || localStorage.getItem('currentResearchId');
      
      if (!activeResearchId) {
        if (scripts && Array.isArray(scripts) && scripts.length > 0) {
          const transformedHooks = transformScriptsToHooks(scripts);
          if (transformedHooks.length > 0) {
            setHooks(transformedHooks);
            setIsInitialLoading(false);
            hasLoadedDataRef.current = true;
            return;
          }
        }
        if (hooks.length === 0) {
          setHooks(getDefaultHooks());
        }
        setIsInitialLoading(false);
        hasLoadedDataRef.current = true;
        return;
      }

      if (scripts && Array.isArray(scripts) && scripts.length > 0) {
        const transformedHooks = transformScriptsToHooks(scripts);
        if (transformedHooks.length > 0) {
          setHooks(transformedHooks);
          setIsInitialLoading(false);
          hasLoadedDataRef.current = true;
          return;
        }
      }

      setIsInitialLoading(true);
      try {
        const response = await apiInvoker.get(API_URLS.USER.GET_RESEARCH(activeResearchId));
        if (response?.success && response?.data?.scripts && Array.isArray(response.data.scripts) && response.data.scripts.length > 0) {
          const transformedHooks = transformScriptsToHooks(response.data.scripts);
          if (transformedHooks.length > 0) {
            setHooks(transformedHooks);
            setIsInitialLoading(false);
            hasLoadedDataRef.current = true;
            return;
          }
        }
        if (hooks.length === 0) {
          setHooks(getDefaultHooks());
        }
        setIsInitialLoading(false);
        hasLoadedDataRef.current = true;
      } catch (err) {
        console.error('Error fetching research:', err);
        if (hooks.length === 0) {
          setHooks(getDefaultHooks());
        }
        setIsInitialLoading(false);
        hasLoadedDataRef.current = true;
      }
    };

    fetchResearchData();
  }, [researchId]);

  const checkPendingScriptGeneration = async () => {
    const pendingScriptStr = localStorage.getItem('pendingScriptGeneration');
    
    if (!pendingScriptStr) {
      return;
    }
    
    try {
      const pendingData = JSON.parse(pendingScriptStr);
      const { researchId: pendingResearchId, scriptOutlineId, formData: pendingFormData, selectedHooks: pendingSelectedHooks, hooks: pendingHooks } = pendingData;
      
      if (!pendingResearchId || !scriptOutlineId) {
        localStorage.removeItem('pendingScriptGeneration');
        return;
      }
      
      if (scriptGenerationTimeoutRef.current) {
        return;
      }
      
      scriptGenerationDataRef.current = pendingData;
      isGeneratingScriptRef.current = true;
      setIsGeneratingScript(true);
      
      try {
        const researchResponse = await apiInvoker.get(API_URLS.USER.GET_RESEARCH(pendingResearchId));
        
        if (researchResponse?.success && researchResponse?.data?.scriptOutline && Array.isArray(researchResponse.data.scriptOutline)) {
          const scriptIndex = parseInt(scriptOutlineId, 10);
          if (!isNaN(scriptIndex) && researchResponse.data.scriptOutline[scriptIndex]) {
            const existingScript = researchResponse.data.scriptOutline[scriptIndex];
            
            if (existingScript && existingScript.content) {
              localStorage.removeItem('pendingScriptGeneration');
              scriptGenerationDataRef.current = null;
              isGeneratingScriptRef.current = false;
              
              navigate('/script-details', {
                state: {
                  formData: pendingFormData,
                  selectedHooks: pendingSelectedHooks,
                  script: existingScript,
                  scriptData: { script: existingScript },
                  researchId: pendingResearchId
                }
              });
              return;
            }
          }
        }
      } catch (researchError) {
        console.error('Error checking research for existing script:', researchError);
      }
      
      const response = await apiInvoker.post(
        API_URLS.USER.GENERATE_RESEARCH_SCRIPT(pendingResearchId),
        { scriptOutlineId },
        { timeout: 600000 }
      );

      if (response?.success && response?.data) {
        const scriptData = response.data;
        
        localStorage.removeItem('pendingScriptGeneration');
        scriptGenerationDataRef.current = null;
        isGeneratingScriptRef.current = false;
        
        navigate('/script-details', {
          state: {
            formData: pendingFormData,
            selectedHooks: pendingSelectedHooks,
            script: scriptData?.script,
            scriptData: scriptData,
            researchId: pendingResearchId
          }
        });
      } else {
        setIsGeneratingScript(false);
        isGeneratingScriptRef.current = false;
        localStorage.removeItem('pendingScriptGeneration');
        scriptGenerationDataRef.current = null;
      }
    } catch (error) {
      console.error('Error checking pending script generation:', error);
      
      const errorMessage = error?.response?.data?.error || error?.message || '';
      const isVersionConflict = errorMessage.includes('No matching document found') || errorMessage.includes('version');
      
      if (isVersionConflict) {
        try {
          const pendingData = JSON.parse(localStorage.getItem('pendingScriptGeneration') || '{}');
          const { researchId: pendingResearchId, scriptOutlineId, formData: pendingFormData, selectedHooks: pendingSelectedHooks } = pendingData;
          
          if (pendingResearchId && scriptOutlineId) {
            const researchResponse = await apiInvoker.get(API_URLS.USER.GET_RESEARCH(pendingResearchId));
            
            if (researchResponse?.success && researchResponse?.data?.scriptOutline && Array.isArray(researchResponse.data.scriptOutline)) {
              const scriptIndex = parseInt(scriptOutlineId, 10);
              if (!isNaN(scriptIndex) && researchResponse.data.scriptOutline[scriptIndex] && researchResponse.data.scriptOutline[scriptIndex].content) {
                const existingScript = researchResponse.data.scriptOutline[scriptIndex];
                
                localStorage.removeItem('pendingScriptGeneration');
                scriptGenerationDataRef.current = null;
                isGeneratingScriptRef.current = false;
                
                navigate('/script-details', {
                  state: {
                    formData: pendingFormData,
                    selectedHooks: pendingSelectedHooks,
                    script: existingScript,
                    scriptData: { script: existingScript },
                    researchId: pendingResearchId
                  }
                });
                return;
              }
            }
          }
        } catch (recoveryError) {
          console.error('Error recovering from version conflict:', recoveryError);
        }
      }
      
      setIsGeneratingScript(false);
      isGeneratingScriptRef.current = false;
      localStorage.removeItem('pendingScriptGeneration');
      scriptGenerationDataRef.current = null;
    }
  };

  useEffect(() => {
    checkPendingScriptGeneration();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isGeneratingScriptRef.current) {
        const pendingScriptStr = localStorage.getItem('pendingScriptGeneration');
        if (pendingScriptStr && scriptGenerationDataRef.current) {
          try {
            const pendingData = JSON.parse(pendingScriptStr);
            const { researchId: pendingResearchId, scriptOutlineId, formData: pendingFormData, selectedHooks: pendingSelectedHooks } = pendingData;
            
            if (scriptGenerationTimeoutRef.current) {
              clearTimeout(scriptGenerationTimeoutRef.current);
              scriptGenerationTimeoutRef.current = null;
            }
            
            try {
              const researchResponse = await apiInvoker.get(API_URLS.USER.GET_RESEARCH(pendingResearchId));
              
              if (researchResponse?.success && researchResponse?.data?.scriptOutline && Array.isArray(researchResponse.data.scriptOutline)) {
                const scriptIndex = parseInt(scriptOutlineId, 10);
                if (!isNaN(scriptIndex) && researchResponse.data.scriptOutline[scriptIndex]) {
                  const existingScript = researchResponse.data.scriptOutline[scriptIndex];
                  
                  if (existingScript && existingScript.content) {
                    localStorage.removeItem('pendingScriptGeneration');
                    scriptGenerationDataRef.current = null;
                    isGeneratingScriptRef.current = false;
                    
                    navigate('/script-details', {
                      state: {
                        formData: pendingFormData,
                        selectedHooks: pendingSelectedHooks,
                        script: existingScript,
                        scriptData: { script: existingScript },
                        researchId: pendingResearchId
                      }
                    });
                    return;
                  }
                }
              }
            } catch (researchError) {
              console.error('Error checking research for existing script:', researchError);
            }
            
            const response = await apiInvoker.post(
              API_URLS.USER.GENERATE_RESEARCH_SCRIPT(pendingResearchId),
              { scriptOutlineId },
              { timeout: 600000 }
            );

            if (response?.success && response?.data) {
              const scriptData = response.data;
              
              localStorage.removeItem('pendingScriptGeneration');
              scriptGenerationDataRef.current = null;
              isGeneratingScriptRef.current = false;
              
              navigate('/script-details', {
                state: {
                  formData: pendingFormData,
                  selectedHooks: pendingSelectedHooks,
                  script: scriptData?.script,
                  scriptData: scriptData,
                  researchId: pendingResearchId
                }
              });
            } else {
              checkPendingScriptGeneration();
            }
          } catch (error) {
            console.error('Error checking script status on visibility change:', error);
            
            const errorMessage = error?.response?.data?.error || error?.message || '';
            const isVersionConflict = errorMessage.includes('No matching document found') || errorMessage.includes('version');
            
            if (isVersionConflict) {
              try {
                const pendingData = JSON.parse(localStorage.getItem('pendingScriptGeneration') || '{}');
                const { researchId: pendingResearchId, scriptOutlineId, formData: pendingFormData, selectedHooks: pendingSelectedHooks } = pendingData;
                
                if (pendingResearchId && scriptOutlineId) {
                  const researchResponse = await apiInvoker.get(API_URLS.USER.GET_RESEARCH(pendingResearchId));
                  
                  if (researchResponse?.success && researchResponse?.data?.scriptOutline && Array.isArray(researchResponse.data.scriptOutline)) {
                    const scriptIndex = parseInt(scriptOutlineId, 10);
                    if (!isNaN(scriptIndex) && researchResponse.data.scriptOutline[scriptIndex] && researchResponse.data.scriptOutline[scriptIndex].content) {
                      const existingScript = researchResponse.data.scriptOutline[scriptIndex];
                      
                      localStorage.removeItem('pendingScriptGeneration');
                      scriptGenerationDataRef.current = null;
                      isGeneratingScriptRef.current = false;
                      
                      navigate('/script-details', {
                        state: {
                          formData: pendingFormData,
                          selectedHooks: pendingSelectedHooks,
                          script: existingScript,
                          scriptData: { script: existingScript },
                          researchId: pendingResearchId
                        }
                      });
                      return;
                    }
                  }
                }
              } catch (recoveryError) {
                console.error('Error recovering from version conflict:', recoveryError);
              }
            }
            
            checkPendingScriptGeneration();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (scriptGenerationTimeoutRef.current) {
        clearTimeout(scriptGenerationTimeoutRef.current);
      }
    };
  }, []);

  const topic = claimedTopics && claimedTopics[0];
  const getKeywordsArray = (keywords) => {
    if (!keywords) {
      return ['Low light', 'Wildlife cameras', 'New trend'];
    }
    
    if (Array.isArray(keywords)) {
      return keywords;
    }
    const supporting = keywords.supporting || [];
    const main = keywords.main || '';
    return main ? [main, ...supporting] : supporting;
  };

  const toggleHook = (hookId) => {
    setSelectedHooks(prev => 
      prev.includes(hookId) 
        ? []
        : [hookId]
    );
  };


  const handleGenerateScript = async () => {
    if (selectedHooks.length === 0) {
      alert('Please select at least one script outline!');
      return;
    }

    if (!researchId) {
      alert('Research ID is missing. Please try again.');
      return;
    }

    setIsGeneratingScript(true);
    isGeneratingScriptRef.current = true;

    const selectedHook = hooks.find(h => h.id === selectedHooks[0]);
    if (!selectedHook) {
      alert('Invalid script selection. Please try again.');
      setIsGeneratingScript(false);
      isGeneratingScriptRef.current = false;
      return;
    }

    const scriptGenerationData = {
      researchId,
      scriptOutlineId: selectedHooks[0],
      formData,
      selectedHooks: hooks.filter(h => selectedHooks.includes(h.id)),
      hooks: hooks
    };

    scriptGenerationDataRef.current = scriptGenerationData;
    localStorage.setItem('pendingScriptGeneration', JSON.stringify(scriptGenerationData));

    try {
      const response = await apiInvoker.post(
        API_URLS.USER.GENERATE_RESEARCH_SCRIPT(researchId),
        { scriptOutlineId: selectedHooks[0] },
        { timeout: 600000 }
      );

      if (response?.success && response?.data) {
        const scriptData = response.data;
        
        localStorage.removeItem('pendingScriptGeneration');
        scriptGenerationDataRef.current = null;
        isGeneratingScriptRef.current = false;
        
        navigate('/script-details', {
          state: {
            formData,
            selectedHooks: hooks.filter(h => selectedHooks.includes(h.id)),
            script: scriptData?.script,
            scriptData: scriptData,
            researchId: researchId
          }
        });
      } else {
        setIsGeneratingScript(false);
        isGeneratingScriptRef.current = false;
        localStorage.removeItem('pendingScriptGeneration');
        scriptGenerationDataRef.current = null;
      }
    } catch (error) {
      console.error('Error generating script:', error);
      
      const errorMessage = error?.response?.data?.error || error?.message || '';
      const isVersionConflict = errorMessage.includes('No matching document found') || errorMessage.includes('version');
      
      if (isVersionConflict) {
        try {
          const researchResponse = await apiInvoker.get(API_URLS.USER.GET_RESEARCH(researchId));
          
          if (researchResponse?.success && researchResponse?.data?.scriptOutline && Array.isArray(researchResponse.data.scriptOutline)) {
            const scriptIndex = parseInt(selectedHooks[0], 10);
            if (!isNaN(scriptIndex) && researchResponse.data.scriptOutline[scriptIndex] && researchResponse.data.scriptOutline[scriptIndex].content) {
              const existingScript = researchResponse.data.scriptOutline[scriptIndex];
              
              localStorage.removeItem('pendingScriptGeneration');
              scriptGenerationDataRef.current = null;
              isGeneratingScriptRef.current = false;
              
              navigate('/script-details', {
                state: {
                  formData,
                  selectedHooks: hooks.filter(h => selectedHooks.includes(h.id)),
                  script: existingScript,
                  scriptData: { script: existingScript },
                  researchId: researchId
                }
              });
              return;
            }
          }
        } catch (recoveryError) {
          console.error('Error recovering from version conflict:', recoveryError);
        }
      }
      
      setIsGeneratingScript(false);
      isGeneratingScriptRef.current = false;
      localStorage.removeItem('pendingScriptGeneration');
      scriptGenerationDataRef.current = null;
    }
  };

  const handleViewDetails = () => {
    setShowTopicDetailsModal(true);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
    };

    const confirmLogout = () => {
      dispatch(logoutUser());
      navigate('/signin');
    };

    const cancelLogout = () => {
      setShowLogoutModal(false);
    };

  const handleEditHook = (hook) => {
    setEditingHook(hook);
    setEditData({
      Title: hook.title?.replace(':', '') || hook.title || '',
      Introduction: hook.introduction || '',
      Conclusion: hook.conclusion || '',
      CallToAction: hook.callToAction || '',
      Sections: hook.sections ? JSON.parse(JSON.stringify(hook.sections)) : []
    });
    setEditingSectionIndex(null);
    setSectionEditData({
      SectionTitle: '',
      Explanation: '',
      StepByStep: [],
      CommonMistakes: [],
      Example: '',
      RetentionMoments: []
    });
  };

  const handleEditSection = (section, index) => {
    setEditingSectionIndex(index);
    setSectionEditData({
      SectionTitle: section.SectionTitle || '',
      Explanation: section.Explanation || '',
      StepByStep: section.StepByStep ? [...section.StepByStep] : [],
      CommonMistakes: section.CommonMistakes ? [...section.CommonMistakes] : [],
      Example: section.Example || '',
      RetentionMoments: section.RetentionMoments ? [...section.RetentionMoments] : []
    });
  };

  const handleSaveSection = () => {
    if (editingSectionIndex === null) return;
    
    const updatedSections = [...editData.Sections];
    updatedSections[editingSectionIndex] = { ...sectionEditData };
    
    setEditData(prev => ({
      ...prev,
      Sections: updatedSections
    }));
    setEditingSectionIndex(null);
    setSectionEditData({
      SectionTitle: '',
      Explanation: '',
      StepByStep: [],
      CommonMistakes: [],
      Example: '',
      RetentionMoments: []
    });
  };

  const handleAddSection = () => {
    const newSection = {
      SectionTitle: '',
      Explanation: '',
      StepByStep: [],
      CommonMistakes: [],
      Example: '',
      RetentionMoments: []
    };
    setEditData(prev => ({
      ...prev,
      Sections: [...prev.Sections, newSection]
    }));
    setEditingSectionIndex(editData.Sections.length);
    setSectionEditData(newSection);
  };

  const handleRemoveSection = (index) => {
    const updatedSections = editData.Sections.filter((_, i) => i !== index);
    setEditData(prev => ({
      ...prev,
      Sections: updatedSections
    }));
    if (editingSectionIndex === index) {
      setEditingSectionIndex(null);
      setSectionEditData({
        SectionTitle: '',
        Explanation: '',
        StepByStep: [],
        CommonMistakes: [],
        Example: '',
        RetentionMoments: []
      });
    }
  };

  const handleAddArrayItem = (fieldName, value = '') => {
    setSectionEditData(prev => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), value]
    }));
  };

  const handleUpdateArrayItem = (fieldName, index, value) => {
    setSectionEditData(prev => {
      const updated = [...(prev[fieldName] || [])];
      updated[index] = value;
      return {
        ...prev,
        [fieldName]: updated
      };
    });
  };

  const handleRemoveArrayItem = (fieldName, index) => {
    setSectionEditData(prev => {
      const updated = [...(prev[fieldName] || [])];
      updated.splice(index, 1);
      return {
        ...prev,
        [fieldName]: updated
      };
    });
  };

  const handleSaveEdit = async () => {
    if (!editingHook) {
      return;
    }

    const activeResearchId = researchId || localStorage.getItem('currentResearchId');
    if (!activeResearchId) {
      alert('Research ID not found. Please try again.');
      return;
    }

    setIsSavingEdit(true);
    try {
      const hookIndex = hooks.findIndex(h => h.id === editingHook.id);
      if (hookIndex === -1) {
        alert('Script outline not found. Please try again.');
        setIsSavingEdit(false);
        return;
      }

      const updatePayload = {
        Introduction: editData.Introduction,
        Conclusion: editData.Conclusion,
        CallToAction: editData.CallToAction,
        Sections: editData.Sections
      };

      if (editData.Title) {
        updatePayload.Title = editData.Title;
      }

      const response = await apiInvoker.put(
        API_URLS.USER.UPDATE_RESEARCH_SCRIPT(activeResearchId, hookIndex),
        updatePayload
      );

      if (response?.success && response?.data) {
        setHooks(hooks.map(h => 
          h.id === editingHook.id 
            ? { 
                ...h, 
                title: editData.Title ? `${editData.Title}:` : h.title,
                introduction: editData.Introduction, 
                conclusion: editData.Conclusion,
                callToAction: editData.CallToAction,
                sections: editData.Sections,
                hook: editData.Introduction,
                text: editData.Introduction,
                originalText: editData.Introduction
              }
            : h
        ));
        setEditingHook(null);
        setEditData({ Title: '', Introduction: '', Conclusion: '', CallToAction: '', Sections: [] });
        setEditingSectionIndex(null);
        setSectionEditData({
          SectionTitle: '',
          Explanation: '',
          StepByStep: [],
          CommonMistakes: [],
          Example: '',
          RetentionMoments: []
        });
      } else {
        alert('Failed to update script outline. Please try again.');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update script outline. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSavingEdit(false);
    }
  };




  return (
    <div className="h-screen bg-main flex overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 overlay-dark z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activePage="create-script"
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="bg-primary text-white py-4 px-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <MobileMenuIcon/>
              </button>
              
              <button 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-white rounded-lg hover:bg-white/10 transition-colors"
              >
                <BackIcon/>
                <span className="font-medium text-sm">Back</span>
              </button>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                {getUserInitials(user)}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">AI Outline</h1>
            <p className="text-gray-600 mb-6">Select script outlines to generate your script</p>
            
            <div className="flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <OutlineIcon/>
                  <h3 className="text-lg font-semibold text-gray-900">Outline</h3>
                </div>

         

                <div className="space-y-4 flex-1">
                  {hooks.length === 0 && !loading && !isInitialLoading && (
                    <div className="flex items-center justify-center py-8">
                      <p className="text-gray-600">No script outlines available</p>
                    </div>
                  )}
                  {hooks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((hook, index) => {
                    const actualIndex = (currentPage - 1) * itemsPerPage + index;
                    const displayTitle = hook.title || `Script Outline ${actualIndex + 1}:`;
                    return (
                      <div key={hook.id}>
                        <div
                          onClick={() => toggleHook(hook.id)}
                          className={`border-2 rounded-xl p-5 transition-all cursor-pointer relative ${
                            selectedHooks.includes(hook.id) ? 'hook-selected' : 'hook-unselected'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <h4 className="font-semibold text-gray-900 text-base flex-1">{displayTitle}</h4>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditHook(hook);
                              }}
                              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0 ml-2"
                            >
                              <EditIcon/>
                              <span className="text-sm font-medium text-gray-700">Edit</span>
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            {hook.introduction && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Introduction</p>
                                <p className="text-sm text-gray-700">{hook.introduction}</p>
                              </div>
                            )}
                            
                            {hook.sections && hook.sections.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Sections</p>
                                {hook.sections.map((section, sIdx) => (
                                  <div key={sIdx} className="mb-3 pl-3 border-l-2 border-gray-200">
                                    <p className="text-sm font-medium text-gray-800 mb-1">{section.SectionTitle}</p>
                                    <p className="text-sm text-gray-600 mb-2">{section.Explanation}</p>
                                    
                                    {section.StepByStep && section.StepByStep.length > 0 && (
                                      <div className="mb-2">
                                        <p className="text-xs font-semibold text-gray-500 mb-1">Steps:</p>
                                        <ul className="list-disc list-inside text-sm text-gray-600">
                                          {section.StepByStep.map((step, stepIdx) => (
                                            <li key={stepIdx}>{step}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    
                                    {section.CommonMistakes && section.CommonMistakes.length > 0 && (
                                      <div className="mb-2">
                                        <p className="text-xs font-semibold text-gray-500 mb-1">Common Mistakes:</p>
                                        <ul className="list-disc list-inside text-sm text-gray-600">
                                          {section.CommonMistakes.map((mistake, mIdx) => (
                                            <li key={mIdx}>{mistake}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    
                                    {section.Example && (
                                      <div className="mb-2">
                                        <p className="text-xs font-semibold text-gray-500 mb-1">Example:</p>
                                        <p className="text-sm text-gray-600 italic">{section.Example}</p>
                                      </div>
                                    )}
                                    
                                    {section.RetentionMoments && section.RetentionMoments.length > 0 && (
                                      <div>
                                        <p className="text-xs font-semibold text-gray-500 mb-1">Retention Moments:</p>
                                        <ul className="list-disc list-inside text-sm text-gray-600">
                                          {section.RetentionMoments.map((moment, rmIdx) => (
                                            <li key={rmIdx}>{moment}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {hook.conclusion && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Conclusion</p>
                                <p className="text-sm text-gray-700">{hook.conclusion}</p>
                              </div>
                            )}
                            
                            {hook.callToAction && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Call To Action</p>
                                <p className="text-sm text-gray-700">{hook.callToAction}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {index < itemsPerPage - 1 && actualIndex < hooks.length - 1 && (
                          <div className="flex items-center justify-center my-8">
                            <div className="flex items-center gap-3 w-full">
                              <div className="flex-1 h-px bg-gray-300"></div>
                              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">Alternative Script Outline</span>
                              <div className="flex-1 h-px bg-gray-300"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {hooks.length > itemsPerPage && (
                    <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {Math.ceil(hooks.length / itemsPerPage)}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(hooks.length / itemsPerPage), prev + 1))}
                        disabled={currentPage >= Math.ceil(hooks.length / itemsPerPage)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleGenerateScript}
                    disabled={selectedHooks.length === 0 || isGeneratingScript}
                    className={`font-semibold text-base text-white transition-all ${
                      selectedHooks.length === 0 || isGeneratingScript
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                        : 'bg-[#653600] hover:bg-[#4a2800] shadow-sm'
                    }`}
                    style={{ width: '402px', height: '56px', borderRadius: '10px' }}
                  >
                    {isGeneratingScript ? 'Generating...' : 'Generate Script'}
                  </button>
                </div>
              </div>
          </div>
        </main>
      </div>

      {editingHook && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 pb-0">
              <h3 className="text-xl font-bold text-gray-900">Edit Script Outline</h3>
              <button
                onClick={() => {
                  setEditingHook(null);
                  setEditData({ Title: '', Introduction: '', Conclusion: '', CallToAction: '', Sections: [] });
                  setEditingSectionIndex(null);
                  setSectionEditData({
                    SectionTitle: '',
                    Explanation: '',
                    StepByStep: [],
                    CommonMistakes: [],
                    Example: '',
                    RetentionMoments: []
                  });
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <CrossIcon/>
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 p-6 pt-4">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={editData.Title}
                  onChange={(e) => setEditData(prev => ({ ...prev, Title: e.target.value }))}
                  className="input-primary text-base w-full"
                  placeholder="Enter script outline title..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Introduction
                </label>
                <textarea
                  value={editData.Introduction}
                  onChange={(e) => setEditData(prev => ({ ...prev, Introduction: e.target.value }))}
                  rows={5}
                  className="input-primary resize-none text-base w-full"
                  placeholder="Enter introduction text..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-gray-900">
                    Sections
                  </label>
                  <button
                    type="button"
                    onClick={handleAddSection}
                    className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium"
                  >
                    + Add Section
                  </button>
                </div>
                <div className="space-y-4">
                  {editData.Sections.map((section, sIdx) => (
                    <div key={sIdx} className="border-2 border-gray-200 rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">Section {sIdx + 1}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSection(sIdx)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <CrossIcon />
                        </button>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                          Section Title
                        </label>
                        <input
                          type="text"
                          value={section.SectionTitle || ''}
                          onChange={(e) => {
                            const updatedSections = [...editData.Sections];
                            updatedSections[sIdx] = { ...updatedSections[sIdx], SectionTitle: e.target.value };
                            setEditData(prev => ({ ...prev, Sections: updatedSections }));
                          }}
                          className="input-primary text-sm w-full"
                          placeholder="Enter section title..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                          Explanation
                        </label>
                        <textarea
                          value={section.Explanation || ''}
                          onChange={(e) => {
                            const updatedSections = [...editData.Sections];
                            updatedSections[sIdx] = { ...updatedSections[sIdx], Explanation: e.target.value };
                            setEditData(prev => ({ ...prev, Sections: updatedSections }));
                          }}
                          rows={3}
                          className="input-primary resize-none text-sm w-full"
                          placeholder="Enter explanation..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                          Example
                        </label>
                        <textarea
                          value={section.Example || ''}
                          onChange={(e) => {
                            const updatedSections = [...editData.Sections];
                            updatedSections[sIdx] = { ...updatedSections[sIdx], Example: e.target.value };
                            setEditData(prev => ({ ...prev, Sections: updatedSections }));
                          }}
                          rows={2}
                          className="input-primary resize-none text-sm w-full"
                          placeholder="Enter example..."
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-semibold text-gray-500">
                            Step By Step
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedSections = [...editData.Sections];
                              updatedSections[sIdx] = { 
                                ...updatedSections[sIdx], 
                                StepByStep: [...(updatedSections[sIdx].StepByStep || []), ''] 
                              };
                              setEditData(prev => ({ ...prev, Sections: updatedSections }));
                            }}
                            className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium"
                          >
                            + Add
                          </button>
                        </div>
                        <div className="space-y-2">
                          {(section.StepByStep || []).map((step, stepIdx) => (
                            <div key={stepIdx} className="flex gap-2 items-center">
                              <span className="text-xs text-gray-500 w-5">{stepIdx + 1}.</span>
                              <input
                                type="text"
                                value={step}
                                onChange={(e) => {
                                  const updatedSections = [...editData.Sections];
                                  const updatedSteps = [...(updatedSections[sIdx].StepByStep || [])];
                                  updatedSteps[stepIdx] = e.target.value;
                                  updatedSections[sIdx] = { ...updatedSections[sIdx], StepByStep: updatedSteps };
                                  setEditData(prev => ({ ...prev, Sections: updatedSections }));
                                }}
                                className="input-primary text-sm flex-1"
                                placeholder={`Step ${stepIdx + 1}...`}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedSections = [...editData.Sections];
                                  const updatedSteps = [...(updatedSections[sIdx].StepByStep || [])];
                                  updatedSteps.splice(stepIdx, 1);
                                  updatedSections[sIdx] = { ...updatedSections[sIdx], StepByStep: updatedSteps };
                                  setEditData(prev => ({ ...prev, Sections: updatedSections }));
                                }}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <CrossIcon />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-semibold text-gray-500">
                            Common Mistakes
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedSections = [...editData.Sections];
                              updatedSections[sIdx] = { 
                                ...updatedSections[sIdx], 
                                CommonMistakes: [...(updatedSections[sIdx].CommonMistakes || []), ''] 
                              };
                              setEditData(prev => ({ ...prev, Sections: updatedSections }));
                            }}
                            className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium"
                          >
                            + Add
                          </button>
                        </div>
                        <div className="space-y-2">
                          {(section.CommonMistakes || []).map((mistake, mIdx) => (
                            <div key={mIdx} className="flex gap-2 items-center">
                              <span className="text-xs text-gray-500">•</span>
                              <input
                                type="text"
                                value={mistake}
                                onChange={(e) => {
                                  const updatedSections = [...editData.Sections];
                                  const updatedMistakes = [...(updatedSections[sIdx].CommonMistakes || [])];
                                  updatedMistakes[mIdx] = e.target.value;
                                  updatedSections[sIdx] = { ...updatedSections[sIdx], CommonMistakes: updatedMistakes };
                                  setEditData(prev => ({ ...prev, Sections: updatedSections }));
                                }}
                                className="input-primary text-sm flex-1"
                                placeholder={`Mistake ${mIdx + 1}...`}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedSections = [...editData.Sections];
                                  const updatedMistakes = [...(updatedSections[sIdx].CommonMistakes || [])];
                                  updatedMistakes.splice(mIdx, 1);
                                  updatedSections[sIdx] = { ...updatedSections[sIdx], CommonMistakes: updatedMistakes };
                                  setEditData(prev => ({ ...prev, Sections: updatedSections }));
                                }}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <CrossIcon />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-semibold text-gray-500">
                            Retention Moments
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedSections = [...editData.Sections];
                              updatedSections[sIdx] = { 
                                ...updatedSections[sIdx], 
                                RetentionMoments: [...(updatedSections[sIdx].RetentionMoments || []), ''] 
                              };
                              setEditData(prev => ({ ...prev, Sections: updatedSections }));
                            }}
                            className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium"
                          >
                            + Add
                          </button>
                        </div>
                        <div className="space-y-2">
                          {(section.RetentionMoments || []).map((moment, rmIdx) => (
                            <div key={rmIdx} className="flex gap-2 items-center">
                              <span className="text-xs text-gray-500">•</span>
                              <input
                                type="text"
                                value={moment}
                                onChange={(e) => {
                                  const updatedSections = [...editData.Sections];
                                  const updatedMoments = [...(updatedSections[sIdx].RetentionMoments || [])];
                                  updatedMoments[rmIdx] = e.target.value;
                                  updatedSections[sIdx] = { ...updatedSections[sIdx], RetentionMoments: updatedMoments };
                                  setEditData(prev => ({ ...prev, Sections: updatedSections }));
                                }}
                                className="input-primary text-sm flex-1"
                                placeholder={`Moment ${rmIdx + 1}...`}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedSections = [...editData.Sections];
                                  const updatedMoments = [...(updatedSections[sIdx].RetentionMoments || [])];
                                  updatedMoments.splice(rmIdx, 1);
                                  updatedSections[sIdx] = { ...updatedSections[sIdx], RetentionMoments: updatedMoments };
                                  setEditData(prev => ({ ...prev, Sections: updatedSections }));
                                }}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <CrossIcon />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                  {editData.Sections.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No sections available. Click "+ Add Section" to create one.</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Conclusion
                </label>
                <textarea
                  value={editData.Conclusion}
                  onChange={(e) => setEditData(prev => ({ ...prev, Conclusion: e.target.value }))}
                  rows={5}
                  className="input-primary resize-none text-base w-full"
                  placeholder="Enter conclusion text..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Call To Action
                </label>
                <textarea
                  value={editData.CallToAction}
                  onChange={(e) => setEditData(prev => ({ ...prev, CallToAction: e.target.value }))}
                  rows={5}
                  className="input-primary resize-none text-base w-full"
                  placeholder="Enter call to action text..."
                />
              </div>
            </div>
            </div>

            <div className="flex gap-3 justify-end p-6 pt-4 border-t border-gray-100">
              <button
                onClick={() => {
                  setEditingHook(null);
                  setEditData({ Title: '', Introduction: '', Conclusion: '', CallToAction: '', Sections: [] });
                  setEditingSectionIndex(null);
                  setSectionEditData({
                    SectionTitle: '',
                    Explanation: '',
                    StepByStep: [],
                    CommonMistakes: [],
                    Example: '',
                    RetentionMoments: []
                  });
                }}
                disabled={isSavingEdit}
                className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSavingEdit}
                className="px-6 py-2.5 btn-primary rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSavingEdit ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTopicDetailsModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-2xl font-bold text-gray-900">TOPIC DETAILS</h3>
              <button
                onClick={() => setShowTopicDetailsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <CrossIcon/>
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 p-6">
              <div className="space-y-5">
              <div>
                <h2 className="text-xl font-bold text-black mb-2">
                  {claimedTopics?.[0]?.title || "What's Redefining Wildlife Photography in 2025"}
                </h2>
                <p className="text-sm text-secondary leading-relaxed">
                  {claimedTopics?.[0]?.description || 'A breakdown of the innovations and trends elevating how creators capture nature in 2025.'}
                </p>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-black">Region & Category</span>
                  <div className="flex items-center text-sm font-semibold text-black">
                    <StarrIcon className="w-4 h-4 text-gray-700 stroke-current fill-none stroke-2"/>
                    <span className="ml-1">{claimedTopics?.[0]?.score || 86}/10 Score</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {claimedTopics?.[0]?.region && (
                    <span className="tag-brown">{claimedTopics[0].region}</span>
                  )}
                  {claimedTopics?.[0]?.category && (
                    <span className="tag-brown">{claimedTopics[0].category}</span>
                  )}
                </div>
              </div>

              {claimedTopics?.[0]?.trend && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Trend</h4>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    claimedTopics[0].trend === 'High' 
                      ? 'bg-green-100 text-green-800' 
                      : claimedTopics[0].trend === 'Medium'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                  }`}>
                    {claimedTopics[0].trend}
                  </span>
                </div>
              )}

              {claimedTopics?.[0]?.suggestedHook && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Suggested Hook</h4>
                  <p className="text-sm text-secondary leading-relaxed">{claimedTopics[0].suggestedHook}</p>
                </div>
              )}

              {claimedTopics?.[0]?.competitorVideos && Array.isArray(claimedTopics[0].competitorVideos) && claimedTopics[0].competitorVideos.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Competitor Videos</h4>
                  <div className="space-y-3">
                    {claimedTopics[0].competitorVideos.map((video, idx) => (
                      <div key={video._id || idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="text-sm font-medium text-black">{video.creator || video.name || 'Unknown Creator'}</p>
                          {video.views && (
                            <span className="text-xs text-secondary whitespace-nowrap">
                              {typeof video.views === 'number' 
                                ? video.views.toLocaleString() 
                                : video.views} views
                            </span>
                          )}
                        </div>
                        {video.videoLink && (
                          <a 
                            href={video.videoLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline break-all"
                          >
                            {video.videoLink}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {claimedTopics?.[0]?.geoTrend && Array.isArray(claimedTopics[0].geoTrend) && claimedTopics[0].geoTrend.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Geo Trend</h4>
                  <div className="flex gap-2 flex-wrap">
                    {claimedTopics[0].geoTrend.map((geo, idx) => (
                      <div key={geo._id || idx} className="border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                        <p className="text-xs font-medium text-black">{geo.country || 'Unknown'}</p>
                        {geo.rating !== undefined && geo.rating !== null && (
                          <div className="flex items-center gap-1 mt-1">
                            <StarrIcon className="w-3 h-3 text-yellow-500 stroke-current stroke-2" />
                            <span className="text-xs text-secondary">{geo.rating}/5</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {getKeywordsArray(claimedTopics?.[0]?.keywords).length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Keywords</h4>
                  <div className="flex gap-2 flex-wrap">
                    {getKeywordsArray(claimedTopics[0].keywords).map((keyword, idx) => (
                      <span key={idx} className="tag-brown">{keyword}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <h4 className="text-sm font-semibold text-black mb-2">Created By</h4>
                <p className="text-sm text-secondary">
                  {claimedTopics?.[0]?.createdBy?.fullName || 
                   claimedTopics?.[0]?.userId?.fullName || 
                   claimedTopics?.[0]?.user?.fullName ||
                   user?.fullName || 
                   'Unknown'}
                </p>
              </div>

              {claimedTopics?.[0]?.status && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Status</h4>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    claimedTopics[0].status === 'available' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {claimedTopics[0].status === 'available' ? 'Available' : 'Claimed'}
                  </span>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      )}
      <LogoutModal
        showLogoutModal={showLogoutModal}
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
      />


      <LoadingModal 
        isLoading={loading || isGeneratingScript || isInitialLoading}
        title={isGeneratingScript ? 'Generating Script' : (isInitialLoading ? 'Loading Outline' : (hooks.length > 0 ? 'Loading Outline' : 'Loading'))}
        message={
          isGeneratingScript 
            ? 'Creating your script... Please wait.'
            : (isInitialLoading
              ? 'Loading outline data... Please wait.'
              : (hooks.length > 0 
                ? 'Fetching outline data...'
                : 'Loading...'))
        }
      />
    </div>
  );
};

export default AIOutline;